import { Controller } from "@hotwired/stimulus"
import { Tooltip } from "bootstrap"

// Croix du bandeau hors production (cf. EnvNoticeComponent) : pendant une heure, la page prend
// l'aspect de la production, le temps d'enregistrer une vidéo ou de faire une présentation ; le
// point laissé à gauche du nom de l'environnement, dans la barre latérale, réactive le bandeau
// avant l'heure. Posé sur la coquille, ancêtre commun des deux. L'échéance vit dans le
// localStorage, donc par navigateur et par domaine. Le script de l'EnvBannerComponent la relit à
// chaque page avant que rien ne soit peint ; ce contrôleur la pose, la propage aux autres
// onglets et rend le bandeau à une page restée ouverte.
const STORAGE_KEY = "depozen:env-notice-hidden-until"
const HIDDEN_FOR_MS = 60 * 60 * 1000

function hiddenUntil() {
  return parseInt(localStorage.getItem(STORAGE_KEY), 10) || 0
}

function isHidden() {
  return document.documentElement.dataset.envNotice === "hidden"
}

// Le panneau principal glisse de la hauteur que perd (ou regagne) le bandeau au lieu de sauter,
// et les teintes se fondent (cf. `env-notice-transitioning` dans _app_shell.scss). Pas de
// transition dans un onglet caché (synchronisé depuis un autre) : elle attendrait, pour
// basculer, une image que l'onglet ne peint pas.
function setHidden(hidden) {
  const root = document.documentElement
  const update = () => {
    if (hidden) root.dataset.envNotice = "hidden"
    else delete root.dataset.envNotice
  }

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  if (typeof document.startViewTransition !== "function" || reduceMotion || document.hidden) {
    update()
    return
  }

  root.classList.add("env-notice-transitioning")
  document.startViewTransition(update).finished.finally(() => {
    root.classList.remove("env-notice-transitioning")
  })
}

export default class extends Controller {
  // La croix et le point.
  static targets = ["toggle"]

  connect() {
    this.sync = this.sync.bind(this)
    this.syncFromStorage = this.syncFromStorage.bind(this)
    window.addEventListener("storage", this.syncFromStorage)
    this.sync()
  }

  disconnect() {
    window.removeEventListener("storage", this.syncFromStorage)
    clearTimeout(this.expiry)
  }

  hide() {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + HIDDEN_FOR_MS))
    this.sync()
  }

  show() {
    localStorage.removeItem(STORAGE_KEY)
    this.sync()
  }

  // `key` est nul quand un autre onglet vide tout le localStorage.
  syncFromStorage(event) {
    if (event.key === STORAGE_KEY || event.key === null) this.sync()
  }

  // Minuterie bornée à une heure : une horloge système reculée entre-temps donnerait un délai
  // au-delà de ce que setTimeout accepte, qui partirait alors immédiatement, en boucle.
  sync() {
    clearTimeout(this.expiry)
    const remaining = hiddenUntil() - Date.now()
    if (remaining > 0) this.expiry = setTimeout(this.sync, Math.min(remaining, HIDDEN_FOR_MS))
    if (isHidden() === remaining > 0) return

    // Croix et point apparaissent et disparaissent, parfois sous un pointeur immobile (clic,
    // échéance, autre onglet) : une infobulle ouverte ne recevrait jamais de mouseleave.
    this.toggleTargets.forEach((button) => Tooltip.getInstance(button)?.hide())
    setHidden(remaining > 0)
  }
}
